import {
  IMarketingCampaign,
  IMarketingCampaignEntry,
  IMarketingCampaignRun,
  IMarketingCampaignRunCost,
  IMarketingCampaignStepConversion,
  IStripeInvoice,
  MarketingCampaignEntryCollection,
  MarketingCampaignEntryStatus,
  MarketingCampaignRunStatus,
  MarketingCampaignStepConversionStatus,
  OrganisationSubscriptionCollection,
} from '@principle-theorem/principle-core/interfaces';
import {
  AtLeast,
  DocumentReference,
  Firestore,
  FirestoreAggregate,
  IReffable,
  Query,
  QueryConstraint,
  Region,
  WithRef,
  all$,
  asReffable,
  collectionGroupQuery,
  toQuery,
  toTimePeriod,
  toTimestampRange,
  undeletedQuery,
  where,
} from '@principle-theorem/shared';
import { sum } from 'lodash';
import * as moment from 'moment-timezone';
import { Observable } from 'rxjs';
import Stripe from 'stripe';
import { StripeInvoice } from '../organisation/stripe-invoice';
import { MarketingCampaign } from './campaign';

export class MarketingCampaignRun {
  static init(
    overrides: AtLeast<IMarketingCampaignRun, 'type'>
  ): IMarketingCampaignRun {
    const defaultRange = toTimePeriod(
      moment().startOf('day'),
      moment().endOf('day')
    );
    return {
      dateRange: toTimestampRange(defaultRange),
      status: MarketingCampaignRunStatus.Draft,
      estimates: {
        costPerEntry: 0,
        newEntries: 0,
        entriesThisRun: 0,
        estimatedCost: 0,
      },
      deleted: false,
      ...overrides,
    };
  }

  static campaignRef(
    campaignRun: IReffable<IMarketingCampaignRun>
  ): DocumentReference<IMarketingCampaign> {
    return Firestore.getParentDocRef<IMarketingCampaign>(campaignRun.ref);
  }

  static stepConversionQuery(
    campaignRun: IReffable<IMarketingCampaignRun>,
    statuses?: MarketingCampaignStepConversionStatus[]
  ): Query<IMarketingCampaignStepConversion> {
    const query = collectionGroupQuery<IMarketingCampaignStepConversion>(
      MarketingCampaignEntryCollection.StepConversions,
      where('campaignRunRef', '==', campaignRun.ref)
    );
    if (statuses?.length) {
      return toQuery(query, where('status', 'in', statuses));
    }
    return query;
  }

  static async getStepConversions(
    campaignRun: WithRef<IMarketingCampaignRun>
  ): Promise<WithRef<IMarketingCampaignStepConversion>[]> {
    return Firestore.getDocs(
      MarketingCampaignRun.stepConversionQuery(campaignRun)
    );
  }

  static async getAvailableEntryCount(
    campaignRun: WithRef<IMarketingCampaignRun>
  ): Promise<number | undefined> {
    const campaignRef = MarketingCampaignRun.campaignRef(campaignRun);
    const campaign = await Firestore.getDoc(campaignRef);
    if (!campaign.maximumEntriesPerRun) {
      return undefined;
    }
    const nonPreviewCount = await Firestore.getCount(
      MarketingCampaignRun.nonPreviewEntriesQuery(campaignRun)
    );
    return campaign.maximumEntriesPerRun - nonPreviewCount;
  }

  static nonPreviewEntriesQuery(
    campaignRun: WithRef<IMarketingCampaignRun>
  ): Query<IMarketingCampaignEntry> {
    return MarketingCampaignRun.entriesQuery(
      campaignRun,
      where('status', '!=', MarketingCampaignEntryStatus.Preview)
    );
  }

  static previewEntriesQuery(
    campaignRun: WithRef<IMarketingCampaignRun>
  ): Query<IMarketingCampaignEntry> {
    return MarketingCampaignRun.entriesQuery(
      campaignRun,
      where('status', '==', MarketingCampaignEntryStatus.Preview)
    );
  }

  static approvedEntriesQuery(
    campaignRun: WithRef<IMarketingCampaignRun>
  ): Query<IMarketingCampaignEntry> {
    return MarketingCampaignRun.entriesQuery(
      campaignRun,
      where('status', '==', MarketingCampaignEntryStatus.Approved)
    );
  }

  static activeEntriesQuery(
    campaignRun: WithRef<IMarketingCampaignRun>
  ): Query<IMarketingCampaignEntry> {
    const inactiveStatuses = [
      MarketingCampaignEntryStatus.Preview,
      MarketingCampaignEntryStatus.Converted,
      MarketingCampaignEntryStatus.Unenrolled,
      MarketingCampaignEntryStatus.Unsubscribed,
      MarketingCampaignEntryStatus.Completed,
    ];
    return MarketingCampaignRun.entriesQuery(
      campaignRun,
      where('status', 'not-in', inactiveStatuses)
    );
  }

  static entriesQuery(
    campaignRun: WithRef<IMarketingCampaignRun>,
    ...queryConstraints: QueryConstraint[]
  ): Query<IMarketingCampaignEntry> {
    const campaignRef = MarketingCampaignRun.campaignRef(campaignRun);
    return toQuery(
      MarketingCampaign.marketingCampaignEntryCol(asReffable(campaignRef)),
      where('deleted', '==', false),
      where('campaignRunRef', '==', campaignRun.ref),
      ...queryConstraints
    );
  }

  static enrolledEntriesQuery(
    campaignRun: WithRef<IMarketingCampaignRun>,
    ...queryConstraints: QueryConstraint[]
  ): Query<IMarketingCampaignEntry> {
    const campaignRef = MarketingCampaignRun.campaignRef(campaignRun);
    return toQuery(
      MarketingCampaign.marketingCampaignEntryCol(asReffable(campaignRef)),
      where('deleted', '==', false),
      where('enrolledByCampaignRunRef', '==', campaignRun.ref),
      ...queryConstraints
    );
  }

  static async getEnrolledEntriesCount(
    campaignRun: WithRef<IMarketingCampaignRun>
  ): Promise<number> {
    return Firestore.getCount(
      MarketingCampaignRun.enrolledEntriesQuery(campaignRun)
    );
  }

  static async getEntriesCount(
    campaignRun: WithRef<IMarketingCampaignRun>,
    status?: MarketingCampaignEntryStatus[]
  ): Promise<number> {
    const constraints: QueryConstraint[] = [];
    if (status && status.length > 0) {
      constraints.push(where('status', 'in', status));
    }
    const entryQuery = MarketingCampaignRun.entriesQuery(
      campaignRun,
      ...constraints
    );
    return Firestore.getCount(entryQuery);
  }

  static async resolveCosts(
    campaignRun: WithRef<IMarketingCampaignRun>
  ): Promise<IMarketingCampaignRunCost> {
    const statuses = [
      MarketingCampaignStepConversionStatus.Queued,
      MarketingCampaignStepConversionStatus.Pending,
    ];
    const estimatesQuery = MarketingCampaignRun.stepConversionQuery(
      campaignRun,
      statuses
    );
    const estimates = await FirestoreAggregate.get(estimatesQuery, {
      estimatedCost: FirestoreAggregate.sum('estimatedCost'),
      estimatedCostRemaining: FirestoreAggregate.sum('estimatedCostRemaining'),
    });

    const incurredQuery = MarketingCampaignRun.stepConversionQuery(campaignRun);
    const incurred = await FirestoreAggregate.get(incurredQuery, {
      stepCount: FirestoreAggregate.count(),
      incurredCost: FirestoreAggregate.sum('incurredCost'),
    });

    return {
      ...estimates,
      ...incurred,
    };
  }

  static async finalise(
    campaignRun: WithRef<IMarketingCampaignRun>
  ): Promise<void> {
    if (!campaignRun.estimates) {
      return;
    }
    await Firestore.patchDoc(campaignRun.ref, {
      status: MarketingCampaignRunStatus.PendingPayment,
    });
    return;

    // TODO: What do we want to do here?
    // const minimumPrepayment =
    //   MarketingCampaignRun.minimumPrepayment(campaignRun);
    // const totalPaid = MarketingCampaignRun.totalPrepaymentPaid(campaignRun);
    // const remainingPrepayment = minimumPrepayment - totalPaid;
    // if (remainingPrepayment > 0) {
    //   await Firestore.patchDoc(campaignRun.ref, {
    //     status: MarketingCampaignRunStatus.PendingPayment,
    //   });
    //   return;
    // }

    // const campaignRef = MarketingCampaignRun.campaignRef(campaignRun);
    // const brandRef = MarketingCampaign.brandRef(asReffable(campaignRef));
    // const orgRef = Brand.organisationRef(asReffable(brandRef));
    // const org = await Firestore.getDoc(orgRef);

    // const campaign = await Firestore.getDoc(campaignRef);

    // // TODO: Add multi subscription handling. Need to calculate cost per practice,
    // // and invocie determine their subscription, if they are athe same subscription.
    // // Then join the payments under one invoice. Otherwise generate invoices per practice
    // const subscription = Organisation.getSubscription(org);
    // if (!subscription) {
    //   // TODO: Add user feedback for "no subscription" error
    //   return;
    // }

    // const stripeInvoice = await StripeAccount.invoiceMarketingPrepayment(
    //   stripe,
    //   subscription,
    //   campaign,
    //   campaignRun,
    //   remainingPrepayment
    // );

    // await Firestore.patchDoc(campaignRun.ref, {
    //   status: MarketingCampaignRunStatus.PendingPayment,
    //   invoices: [...campaignRun.invoices, stripeInvoice],
    // });
  }

  static invoiceQuery(
    campaignRun: WithRef<IMarketingCampaignRun>
  ): Query<IStripeInvoice> {
    return toQuery(
      collectionGroupQuery<IStripeInvoice>(
        OrganisationSubscriptionCollection.StripeInvoices
      ),
      where('campaignRunRef', '==', campaignRun.ref)
    );
  }

  static async getInvoices(
    campaignRun: WithRef<IMarketingCampaignRun>
  ): Promise<WithRef<IStripeInvoice>[]> {
    return Firestore.getDocs(MarketingCampaignRun.invoiceQuery(campaignRun));
  }

  static getInvoices$(
    campaignRun: WithRef<IMarketingCampaignRun>
  ): Observable<WithRef<IStripeInvoice>[]> {
    return all$(MarketingCampaignRun.invoiceQuery(campaignRun));
  }

  static async totalPrepaymentInvoiced(
    campaignRun: WithRef<IMarketingCampaignRun>
  ): Promise<number> {
    const invoices = await MarketingCampaignRun.getInvoices(campaignRun);
    const invoiceTotals = invoices
      .filter((invoice) => !StripeInvoice.isVoid(invoice))
      .map((invoice) => invoice.invoice.total);
    return sum(invoiceTotals);
  }

  static async totalPrepaymentPaid(
    campaignRun: WithRef<IMarketingCampaignRun>
  ): Promise<number> {
    const invoices = await MarketingCampaignRun.getInvoices(campaignRun);
    const paidAmounts = invoices
      .filter((invoice) => !StripeInvoice.isVoid(invoice))
      .map((invoice) => invoice.invoice.amountPaid);
    return sum(paidAmounts);
  }

  static minimumPrepayment(
    campaignRun: WithRef<IMarketingCampaignRun>
  ): number {
    // TODO: Does not allow a practice/brand to set their own marketing budget.
    if (campaignRun.estimates.estimatedCost <= CAMPAIGN_RUN_OVERDRAW_LIMIT) {
      return 0;
    }
    return campaignRun.estimates.estimatedCost;
  }

  static async cancel(
    campaignRun: WithRef<IMarketingCampaignRun>
  ): Promise<void> {
    if (campaignRun.status !== MarketingCampaignRunStatus.PendingPayment) {
      return;
    }
    await Firestore.patchDoc(campaignRun.ref, {
      status: MarketingCampaignRunStatus.Cancelled,
    });
  }

  static hasStarted(campaignRun: IMarketingCampaignRun): boolean {
    const pendingStatuses = [
      MarketingCampaignRunStatus.Draft,
      MarketingCampaignRunStatus.PendingPayment,
    ];
    return !pendingStatuses.includes(campaignRun.status);
  }

  static async shouldCompleteCampaignRun(
    campaignRun: WithRef<IMarketingCampaignRun>
  ): Promise<boolean> {
    const previewCount = await MarketingCampaignRun.getEntriesCount(
      campaignRun,
      [MarketingCampaignEntryStatus.Preview]
    );

    if (!previewCount) {
      return true;
    }

    return false;
  }

  static async completeCampaignRun(
    campaignRun: IReffable<IMarketingCampaignRun>
  ): Promise<void> {
    await Firestore.patchDoc(campaignRun.ref, {
      status: MarketingCampaignRunStatus.Complete,
    });
  }

  static previewDuplicateEmailEntriesQuery(
    campaignRun: IReffable<IMarketingCampaignRun>
  ): Query<IMarketingCampaignEntry> {
    const campaignRef = MarketingCampaignRun.campaignRef(campaignRun);
    return toQuery(
      undeletedQuery(
        MarketingCampaign.marketingCampaignEntryCol(asReffable(campaignRef))
      ),
      where('campaignRunRef', '==', campaignRun.ref),
      where('hasDuplicateEmail', '==', true),
      where('status', '==', MarketingCampaignEntryStatus.Preview)
    );
  }

  static previewDuplicateMobileEntriesQuery(
    campaignRun: IReffable<IMarketingCampaignRun>
  ): Query<IMarketingCampaignEntry> {
    const campaignRef = MarketingCampaignRun.campaignRef(campaignRun);
    return toQuery(
      undeletedQuery(
        MarketingCampaign.marketingCampaignEntryCol(asReffable(campaignRef))
      ),
      where('campaignRunRef', '==', campaignRun.ref),
      where('hasDuplicateMobile', '==', true),
      where('status', '==', MarketingCampaignEntryStatus.Preview)
    );
  }
}

// TODO: Probably want this as a setting, either on principle root.
// This is currently $300, as invoices under this will be applied as debit balance in Stripe
export const CAMPAIGN_RUN_OVERDRAW_LIMIT = 300;
export const CAMPAIGN_MIN_PREPAYMENT_AMOUNT = 100;

export const REGION_SMS_SEGMENT_COST: Record<Region, number> = {
  [Region.Australia]: 0.12,
  [Region.NewZealand]: 0.15,
};

export function getDefaultSegmentCostByRegion(region: Region): number {
  return REGION_SMS_SEGMENT_COST[region];
}

export type StripeInvoiceMetadata = Stripe.MetadataParam & {
  marketingCampaignRef?: string;
  marketingCampaignRunRef?: string;
};

export type StripeCreditGrantMetadata = Stripe.MetadataParam & {
  fromStripeInvoiceId?: string;
  marketingCampaignRef?: string;
  marketingCampaignRunRef?: string;
};
